import { useEffect } from "react"

/**
 * Per-quiz step-image URLs, resolved at build time by Vite's glob. Each entry is a
 * quiz folder under `src/assets/img`; checkout images are excluded on purpose —
 * they belong to a later screen and would just waste bandwidth on the cover. The
 * `/src/...` paths are project-root-absolute (the `@` alias isn't supported inside
 * glob patterns) and each pattern must be a static literal, so quiz folders are
 * listed explicitly rather than interpolated.
 */
const QUIZ_GLOBS: Record<string, Record<string, string>> = {
  soulmate: import.meta.glob("/src/assets/img/soulmate/**/*.{png,webp,jpg}", {
    eager: true,
    query: "?url",
    import: "default",
  }),
  "astro-map": import.meta.glob("/src/assets/img/astro-map/**/*.{png,webp,jpg}", {
    eager: true,
    query: "?url",
    import: "default",
  }),
  aura: import.meta.glob("/src/assets/img/aura/**/*.{png,webp,jpg}", {
    eager: true,
    query: "?url",
    import: "default",
  }),
  compatibility: import.meta.glob("/src/assets/img/compatibility/**/*.{png,webp,jpg}", {
    eager: true,
    query: "?url",
    import: "default",
  }),
}

function urlsForQuiz(folder: string): string[] {
  const glob = QUIZ_GLOBS[folder]
  if (!glob) return []
  return Object.entries(glob)
    .filter(([path]) => !path.includes("/checkout/"))
    .map(([, url]) => url)
}

/** Folders already warmed this session — so the warm runs once per quiz, not on
 *  every cover mount. The image handles are kept alive so the in-flight downloads
 *  aren't garbage-collected before they finish. */
const warmedFolders = new Set<string>()
let warmedImages: HTMLImageElement[] = []

/**
 * Warm a quiz's step images into the browser cache. Call from the first screen
 * (cover): while the user reads it, later steps' images download in the background,
 * so each step no longer flash-loads its image on entry. Decode is left to the
 * browser; we only need the bytes cached.
 *
 * @param folder quiz asset folder under `src/assets/img` (e.g. `"soulmate"`,
 *   `"astro-map"`).
 */
export function usePreloadQuizImages(folder: string) {
  useEffect(() => {
    if (warmedFolders.has(folder)) return
    warmedFolders.add(folder)
    warmedImages = warmedImages.concat(
      urlsForQuiz(folder).map((src) => {
        const img = new Image()
        img.src = src
        return img
      }),
    )
  }, [folder])
}
